import { Grid } from '@mui/material';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectIsEligible } from 'selectors/partnerStatusSelector';
import { Features } from 'reducers/partnerStatusReducer';
import { FeatureEligibility } from 'pco-product-service-internal-consumer';
import { cardStyle } from 'views/OrderNewProductOrService/OrderNewProductOrService';
import CardBodyComponent from '../common/card/CardBodyComponent';
import CardHeaderComponent from '../common/card/CardHeaderComponent';
import CardTooltipButton from '../common/card/CardTooltipButton';
import { RootState } from 'store';

interface CreateMandateProposalProps {
  partnerId?: string;
  brUid?: string;
}

const CreateMandateProposal = ({ partnerId }: CreateMandateProposalProps) => {
  const isEligibleForDMStore = useSelector(selectIsEligible) as
    | (FeatureEligibility[] & { message?: string; messages?: string[] })
    | undefined;

  const MSD_URL = useSelector((state: RootState) => state.appConfig.MSD_URL);

  const handleDiscretionaryMandateClick = () => {
    if (partnerId) {
      window?.open(`${MSD_URL}/#/msd/offerings?create-for-prospect=${partnerId}`, '_blank')?.focus();
    }
  };

  const isEligibleForDM = useMemo<FeatureEligibility | undefined>(() => {
    if (isEligibleForDMStore?.message) return undefined;
    return (
      isEligibleForDMStore?.find((eligibility) => eligibility.feature === Features.CREATE_MANDATE_PROPOSAL) ?? undefined
    );
  }, [isEligibleForDMStore]);

  const title = useMemo<React.ReactNode>(() => {
    if (isEligibleForDMStore?.messages) {
      const messages = isEligibleForDMStore.messages;
      return (
        <>
          <div>{partnerId ? 'Partner' : 'Business relation '} is missing following data:</div>
          {messages.map((message, index) => (
            <div key={index}>{message}</div>
          ))}
        </>
      );
    }
    return `${partnerId ? 'Partner' : 'Business relation'} not eligible to Create a Mandate Proposal.`;
  }, [isEligibleForDMStore, partnerId]);

  return (
    <Grid data-testid="create-mandate-proposal" item sx={cardStyle}>
      <CardHeaderComponent title={'Create mandate proposal'} />
      <CardBodyComponent
        content={'Create a proposal for an investment mandate using the Mandate Solution Designer (MSD).'}
      />
      <CardTooltipButton
        id={'discretionary-mandate-button'}
        buttonText={'Open MSD'}
        isEligible={isEligibleForDM?.isEligible ?? false}
        handleClick={handleDiscretionaryMandateClick}
        tooltipTitle={title}
      />
    </Grid>
  );
};

export default CreateMandateProposal;
